import React, { useRef, useState } from 'react';
import { Button, Card, Input, Select, Space, Typography, message } from 'antd';
import { PageContainer } from '@ant-design/pro-components';
import { PlayCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import axiosApiInstance from '../../../../api/axiosClient';
import { auth } from '../../../../config/firebase';

type LogLine = {
    time?: string;
    type: string;
    message: string;
    upc?: string;
    release_name?: string;
    asset_label?: string;
};

type AssignSummary = {
    total_upcs: number;
    assigned: number;
    already_assigned: number;
    missing: number;
    asset_label: string;
    asset_label_id: number;
};

const logColor = (type: string): string => {
    switch (type) {
        case 'assigned':
            return '#3f8600';
        case 'already_assigned':
            return '#d48806';
        case 'missing':
        case 'error':
            return '#cf1322';
        case 'done':
            return '#1677ff';
        default:
            return 'inherit';
    }
};

const AssignAssetLabelByUPC: React.FC = () => {
    const [upcs, setUpcs] = useState('');
    const [assetLabel, setAssetLabel] = useState<string | undefined>();
    const [running, setRunning] = useState(false);
    const [logs, setLogs] = useState<LogLine[]>([]);
    const [summary, setSummary] = useState<AssignSummary | null>(null);
    const logEndRef = useRef<HTMLDivElement>(null);

    const { data: assetLabels = [], isLoading: loadingLabels } = useQuery({
        queryKey: ['asset_labels', 'tools'],
        queryFn: async () => {
            const { data } = await axiosApiInstance.get<API.MusicDistribution.AssetLabel[]>(
                '/api/products/asset_labels/',
            );
            return data ?? [];
        },
    });

    const appendLog = (line: LogLine) => {
        setLogs((prev) => [...prev, line]);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    };

    const handleRun = async () => {
        const label = assetLabel?.trim();
        if (!label) {
            message.warning('Choose an asset label');
            return;
        }
        if (!upcs.trim()) {
            message.warning('Enter at least one UPC');
            return;
        }

        setRunning(true);
        setLogs([]);
        setSummary(null);
        try {
            const token = await auth.currentUser?.getIdToken();
            const resp = await fetch(`${process.env.REACT_APP_API}/api/products/releases/assign-asset-label-by-upc`, {
                method: 'POST',
                headers: {
                    Accept: 'application/x-ndjson',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ upcs, asset_label: label }),
            });
            if (!resp.ok || !resp.body) {
                const text = await resp.text();
                let errMsg = text;
                try {
                    errMsg = JSON.parse(text).error || text;
                } catch {
                    // keep text
                }
                throw new Error(errMsg || `HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split('\n');
                buf = lines.pop() || '';
                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const ev = JSON.parse(line) as LogLine & { type: string; result?: AssignSummary };
                        if (ev.type === 'result' && ev.result) {
                            setSummary(ev.result);
                            continue;
                        }
                        appendLog(ev);
                    } catch {
                        appendLog({ type: 'info', message: line });
                    }
                }
            }
            if (buf.trim()) {
                try {
                    const ev = JSON.parse(buf) as { type: string; result?: AssignSummary };
                    if (ev.type === 'result' && ev.result) {
                        setSummary(ev.result);
                    } else {
                        appendLog(JSON.parse(buf));
                    }
                } catch {
                    appendLog({ type: 'info', message: buf });
                }
            }
            message.success('Assignment finished');
        } catch (err: any) {
            message.error(err?.message || 'Assignment failed');
            appendLog({ type: 'error', message: err?.message || 'Assignment failed' });
        } finally {
            setRunning(false);
        }
    };

    return (
        <PageContainer header={{ title: 'Assign asset label by UPC', ghost: true }}>
            <Typography.Paragraph>
                Paste UPCs separated by commas or new lines. Only releases with no asset label are updated; releases
                that already have one are listed in the log and left unchanged.
            </Typography.Paragraph>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Select
                    showSearch
                    allowClear
                    placeholder="Asset label"
                    style={{ width: '100%', maxWidth: 480 }}
                    loading={loadingLabels}
                    value={assetLabel}
                    onChange={setAssetLabel}
                    options={assetLabels.map((al) => ({
                        value: al.name,
                        label: al.name,
                    }))}
                    filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                />

                <Input.TextArea
                    rows={8}
                    placeholder={'3618022618843\n3618023871872\nor comma-separated on one line'}
                    value={upcs}
                    onChange={(e) => setUpcs(e.target.value)}
                />

                <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    loading={running}
                    disabled={running}
                    onClick={handleRun}
                >
                    Assign asset label
                </Button>

                {summary && (
                    <Card size="small" title="Summary">
                        <Typography.Text>
                            {summary.asset_label} (id {summary.asset_label_id}) · UPCs: {summary.total_upcs} · Assigned:{' '}
                            {summary.assigned} · Already assigned: {summary.already_assigned} · Missing: {summary.missing}
                        </Typography.Text>
                    </Card>
                )}

                <Card size="small" title="Log">
                    <div
                        style={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: 12,
                            background: 'rgba(0,0,0,0.04)',
                            padding: 12,
                            minHeight: 180,
                            maxHeight: 420,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {logs.length === 0 && (
                            <Typography.Text type="secondary">
                                Logs appear here (assigned, already assigned, missing…)
                            </Typography.Text>
                        )}
                        {logs.map((line, i) => (
                            <div key={i} style={{ color: logColor(line.type) }}>
                                {line.time ? `${line.time}  ` : ''}
                                [{line.type}] {line.upc ? `${line.upc}  ` : ''}
                                {line.message}
                                {line.release_name ? `  (${line.release_name})` : ''}
                            </div>
                        ))}
                        <div ref={logEndRef} />
                    </div>
                </Card>
            </Space>
        </PageContainer>
    );
};

export default AssignAssetLabelByUPC;
